/**
 * Service Providers — admin directory of community-curated local vendors
 * (plumbers, electricians, tutors, AC techs, dabbawalas, etc.). Residents
 * browse + rate; admins moderate, verify and feature.
 *
 * NOT to be confused with /vendors (society-contracted maintenance with SLAs
 * and invoices).
 *
 * Tabs:
 *   - all          : active providers (is_active=true)
 *   - verified     : is_society_verified=true + active
 *   - pending      : is_society_verified=false + active
 *   - featured     : featured=true + active
 *   - suggestions  : is_active=false  (resident-submitted, awaiting approval)
 *
 * Detail drawer (right-side modal) exposes contact + verify/feature/deactivate
 * controls, the full rating list with hide/unhide moderation, and a photo
 * uploader. Photo bytes are streamed through the auth-gated GET, so the
 * frontend fetches + creates an object URL just like the staff page.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Plus,
  Search,
  Star,
  ShieldCheck,
  Sparkles,
  EyeOff,
  Eye,
  Phone,
  MessageCircle,
  Mail,
  MapPin,
  Edit3,
  Trash2,
  ImageIcon,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { api, get, post, patch, del, getErrorMessage } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { TabsPills } from '@/components/ui/tabs-pills';
import { EmptyState } from '@/components/ui/empty-state';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CursorPaginator } from '@/components/ui/cursor-paginator';

// ─── types ────────────────────────────────────────────────────────────────

interface CategoryLite {
  id: string;
  code: string;
  name: string;
  icon_name: string | null;
}
interface CategoryRow extends CategoryLite {
  is_active: boolean;
  sort_order: number;
  _count?: { providers: number };
}
interface ProviderRow {
  id: string;
  category_id: string;
  name: string;
  business_name: string | null;
  primary_phone: string;
  secondary_phone: string | null;
  whatsapp: string | null;
  email: string | null;
  address: string | null;
  service_area: string | null;
  rate_card: string | null;
  availability: string | null;
  emergency_available: boolean;
  experience_years: number | null;
  languages_spoken: string | null;
  is_society_verified: boolean;
  verified_at: string | null;
  featured: boolean;
  notes: string | null;
  photo_storage_key: string | null;
  rating_avg: number | null;
  rating_count: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  category?: CategoryLite;
}
interface RatingRow {
  id: string;
  provider_id: string;
  rater_user_id: string;
  rater_unit_id: string | null;
  stars: number;
  review: string | null;
  is_hidden: boolean;
  hidden_reason: string | null;
  created_at: string;
}
interface ProviderListResp {
  items: ProviderRow[];
  next_cursor: string | null;
}
interface CategoryListResp {
  items: CategoryRow[];
  next_cursor: string | null;
}
interface RatingListResp {
  items: RatingRow[];
  next_cursor: string | null;
}
type DetailResp = ProviderRow & { ratings: RatingRow[]; my_rating: RatingRow | null };

// ─── schema for add/edit form ────────────────────────────────────────────

const PHONE_RE = /^[0-9+()\-\s]{7,20}$/;

const ProviderFormSchema = z.object({
  category_id: z.string().uuid('Pick a category'),
  name: z.string().trim().min(2, 'Name is required').max(120),
  business_name: z.string().trim().max(160).optional(),
  primary_phone: z.string().trim().regex(PHONE_RE, 'Invalid phone'),
  secondary_phone: z.string().trim().regex(PHONE_RE).optional().or(z.literal('')),
  whatsapp: z.string().trim().regex(PHONE_RE).optional().or(z.literal('')),
  email: z.string().trim().email('Invalid email').optional().or(z.literal('')),
  address: z.string().trim().max(500).optional(),
  service_area: z.string().trim().max(300).optional(),
  rate_card: z.string().trim().max(1000).optional(),
  availability: z.string().trim().max(300).optional(),
  emergency_available: z.boolean(),
  experience_years: z.coerce.number().int().min(0).max(80).optional().or(z.nan()),
  languages_spoken: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(1000).optional(),
  is_active: z.boolean(),
  featured: z.boolean()
});
type ProviderForm = z.infer<typeof ProviderFormSchema>;

const DEFAULT_FORM: ProviderForm = {
  category_id: '',
  name: '',
  business_name: '',
  primary_phone: '',
  secondary_phone: '',
  whatsapp: '',
  email: '',
  address: '',
  service_area: '',
  rate_card: '',
  availability: '',
  emergency_available: false,
  experience_years: undefined as unknown as number,
  languages_spoken: '',
  notes: '',
  is_active: true,
  featured: false
};

const PAGE_SIZE = 24;

type Tab = 'all' | 'verified' | 'pending' | 'featured' | 'suggestions';

// ─── tiny presentational helpers ─────────────────────────────────────────

function StarsInline({ avg, count }: { avg: number | null; count: number }): JSX.Element {
  if (!avg || count === 0) {
    return <span className="text-vt-12 text-vt-text-muted">No ratings</span>;
  }
  return (
    <span className="inline-flex items-center gap-1 text-vt-12 text-vt-text">
      <Star className="h-3.5 w-3.5 fill-yellow-500 text-yellow-500" />
      <span className="font-semibold">{avg.toFixed(1)}</span>
      <span className="text-vt-text-muted">({count})</span>
    </span>
  );
}

/**
 * Auth-gated <img>. Hits the stream endpoint via axios with the Bearer token,
 * converts to a blob URL, swaps the src. Falls back to a plain placeholder
 * when there's no photo or the fetch fails.
 */
function ProviderPhoto({
  providerId,
  hasPhoto,
  size = 56
}: {
  providerId: string;
  hasPhoto: boolean;
  size?: number;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoke: string | null = null;
    let cancelled = false;
    if (!hasPhoto) return;
    api
      .get<Blob>(`/service-providers/${providerId}/photo`, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        const blobUrl = URL.createObjectURL(res.data);
        revoke = blobUrl;
        setUrl(blobUrl);
      })
      .catch(() => {
        /* swallow — render placeholder */
      });
    return () => {
      cancelled = true;
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [providerId, hasPhoto]);

  return url ? (
    <img
      src={url}
      alt=""
      width={size}
      height={size}
      className="rounded-vt-md object-cover"
      style={{ width: size, height: size }}
    />
  ) : (
    <div
      className="flex items-center justify-center rounded-vt-md bg-vt-surface-2 text-vt-text-muted"
      style={{ width: size, height: size }}
    >
      <ImageIcon className="h-5 w-5" strokeWidth={1.5} />
    </div>
  );
}

// ─── page ────────────────────────────────────────────────────────────────

export function ServiceProvidersPage(): JSX.Element {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<ProviderRow | null>(null);
  const [drawerForId, setDrawerForId] = useState<string | null>(null);
  const [confirmDeleteFor, setConfirmDeleteFor] = useState<ProviderRow | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  // Reset cursor whenever filters change
  const filtersHash = `${tab}|${searchDebounced}|${categoryFilter}`;
  const [cursor, setCursor] = useState<string | null>(null);
  const [accumulated, setAccumulated] = useState<ProviderRow[]>([]);
  useEffect(() => {
    setCursor(null);
    setAccumulated([]);
  }, [filtersHash]);

  const listParams = useMemo(() => {
    const p: Record<string, string | number | boolean> = { limit: PAGE_SIZE };
    if (searchDebounced) p.search = searchDebounced;
    if (categoryFilter) p.category_id = categoryFilter;
    switch (tab) {
      case 'all':
        p.is_active = true;
        break;
      case 'verified':
        p.is_active = true;
        p.is_society_verified = true;
        break;
      case 'pending':
        p.is_active = true;
        p.is_society_verified = false;
        break;
      case 'featured':
        p.is_active = true;
        p.featured = true;
        break;
      case 'suggestions':
        p.is_active = false;
        break;
    }
    return p;
  }, [tab, searchDebounced, categoryFilter]);

  // ─── data ───
  const categoriesQuery = useQuery({
    queryKey: ['service-provider-categories', 'all'],
    queryFn: () => get<CategoryListResp>('/service-provider-categories', { params: { limit: 100 } }),
    staleTime: 60 * 1000
  });
  const categories = categoriesQuery.data?.items ?? [];
  const categoryById = useMemo(() => {
    const m = new Map<string, CategoryRow>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  const listQuery = useQuery({
    queryKey: ['service-providers', filtersHash, cursor],
    queryFn: () =>
      get<ProviderListResp>('/service-providers', {
        params: { ...listParams, ...(cursor ? { cursor } : {}) }
      }),
    placeholderData: (prev) => prev
  });
  useEffect(() => {
    if (!listQuery.data) return;
    setAccumulated((prev) => {
      if (cursor === null) return listQuery.data!.items;
      const seen = new Set(prev.map((p) => p.id));
      return [...prev, ...listQuery.data!.items.filter((it) => !seen.has(it.id))];
    });
  }, [listQuery.data, cursor]);

  const nextCursor = listQuery.data?.next_cursor ?? null;

  // KPIs (only over the loaded page — cheap enough for V1, ok up to ~500 rows)
  const kpis = useMemo(() => {
    const total = accumulated.length;
    const verified = accumulated.filter((p) => p.is_society_verified).length;
    const featured = accumulated.filter((p) => p.featured).length;
    const byCategory = new Map<string, number>();
    for (const p of accumulated) {
      byCategory.set(p.category_id, (byCategory.get(p.category_id) ?? 0) + 1);
    }
    const top: Array<{ category: CategoryRow; count: number }> = [];
    for (const [cid, count] of byCategory.entries()) {
      const cat = categoryById.get(cid);
      if (cat) top.push({ category: cat, count });
    }
    top.sort((a, b) => b.count - a.count);
    return { total, verified, featured, top: top.slice(0, 6) };
  }, [accumulated, categoryById]);

  // ─── form ───
  const form = useForm<ProviderForm>({
    resolver: zodResolver(ProviderFormSchema),
    defaultValues: DEFAULT_FORM,
    mode: 'onChange'
  });

  const openCreate = (): void => {
    setEditing(null);
    form.reset({
      ...DEFAULT_FORM,
      category_id: categories.find((c) => c.is_active)?.id ?? ''
    });
    setOpen(true);
  };
  const openEdit = (p: ProviderRow): void => {
    setEditing(p);
    form.reset({
      category_id: p.category_id,
      name: p.name,
      business_name: p.business_name ?? '',
      primary_phone: p.primary_phone,
      secondary_phone: p.secondary_phone ?? '',
      whatsapp: p.whatsapp ?? '',
      email: p.email ?? '',
      address: p.address ?? '',
      service_area: p.service_area ?? '',
      rate_card: p.rate_card ?? '',
      availability: p.availability ?? '',
      emergency_available: p.emergency_available,
      experience_years: p.experience_years ?? (undefined as unknown as number),
      languages_spoken: p.languages_spoken ?? '',
      notes: p.notes ?? '',
      is_active: p.is_active,
      featured: p.featured
    });
    setOpen(true);
  };

  const save = useMutation({
    mutationFn: async (v: ProviderForm) => {
      const payload = { ...v };
      if (Number.isNaN(payload.experience_years as unknown as number)) {
        payload.experience_years = undefined as unknown as number;
      }
      if (editing) {
        return patch<ProviderRow>(`/service-providers/${editing.id}`, payload);
      }
      return post<ProviderRow>('/service-providers', payload);
    },
    onSuccess: () => {
      toast.success(editing ? 'Provider updated' : 'Provider added');
      setOpen(false);
      setCursor(null);
      void qc.invalidateQueries({ queryKey: ['service-providers'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const remove = useMutation({
    mutationFn: (id: string) => del(`/service-providers/${id}`),
    onSuccess: () => {
      toast.success('Provider deleted');
      setConfirmDeleteFor(null);
      setDrawerForId(null);
      setCursor(null);
      void qc.invalidateQueries({ queryKey: ['service-providers'] });
    },
    onError: (e) => {
      toast.error(getErrorMessage(e));
      setConfirmDeleteFor(null);
    }
  });

  const verify = useMutation({
    mutationFn: (id: string) => post(`/service-providers/${id}/verify`, {}),
    onSuccess: (_d, id) => {
      toast.success('Provider verified');
      setCursor(null);
      void qc.invalidateQueries({ queryKey: ['service-providers'] });
      void qc.invalidateQueries({ queryKey: ['service-provider-detail', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const toggleFeature = useMutation({
    mutationFn: (args: { id: string; featured: boolean }) =>
      post(`/service-providers/${args.id}/feature`, { featured: args.featured }),
    onSuccess: (_d, args) => {
      toast.success(args.featured ? 'Provider featured' : 'Removed from featured');
      setCursor(null);
      void qc.invalidateQueries({ queryKey: ['service-providers'] });
      void qc.invalidateQueries({ queryKey: ['service-provider-detail', args.id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const deactivate = useMutation({
    mutationFn: (id: string) => post(`/service-providers/${id}/deactivate`, {}),
    onSuccess: (_d, id) => {
      toast.success('Provider deactivated');
      setCursor(null);
      void qc.invalidateQueries({ queryKey: ['service-providers'] });
      void qc.invalidateQueries({ queryKey: ['service-provider-detail', id] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  // ─── render ───
  return (
    <div className="flex flex-col gap-vt-4">
      <PageHeader
        title="Service providers"
        description="Community-curated directory of plumbers, electricians, AC techs, tutors and other local providers. Residents browse + rate; you moderate."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add provider
          </Button>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-vt-3 md:grid-cols-4">
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Total (loaded)</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{kpis.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Verified</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{kpis.verified}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Featured</div>
            <div className="mt-1 text-vt-24 font-semibold text-vt-text">{kpis.featured}</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-vt-4">
            <div className="text-vt-12 text-vt-text-muted">Top categories</div>
            <div className="mt-1 flex flex-wrap gap-1">
              {kpis.top.length === 0 ? (
                <span className="text-vt-12 text-vt-text-muted">—</span>
              ) : (
                kpis.top.map((t) => (
                  <Badge key={t.category.id} variant="muted">
                    {t.category.name} · {t.count}
                  </Badge>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="p-vt-3 flex flex-col gap-vt-3 md:flex-row md:items-end md:justify-between">
          <TabsPills
            value={tab}
            onChange={(v) => setTab(v as Tab)}
            options={[
              { value: 'all', label: 'All' },
              { value: 'verified', label: 'Verified' },
              { value: 'pending', label: 'Pending' },
              { value: 'featured', label: 'Featured' },
              { value: 'suggestions', label: 'Resident suggestions' }
            ]}
            aria-label="Provider tabs"
          />
          <div className="flex flex-col gap-vt-2 md:flex-row md:items-center">
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="sp-search" className="sr-only">
                Search
              </Label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-vt-text-muted" />
                <Input
                  id="sp-search"
                  placeholder="Search name, business, phone…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 w-full md:w-64"
                />
              </div>
            </div>
            <div className="flex flex-col gap-vt-1">
              <Label htmlFor="sp-cat" className="sr-only">
                Category
              </Label>
              <Select
                id="sp-cat"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All categories</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Provider grid */}
      <Card>
        <CardContent className="p-vt-4">
          {listQuery.isLoading ? (
            <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : accumulated.length === 0 ? (
            <EmptyState
              title="No providers in this view"
              description={
                tab === 'suggestions'
                  ? 'No resident-submitted suggestions are pending.'
                  : 'Add your first provider, or change the filter.'
              }
              action={
                <Button onClick={openCreate}>
                  <Plus className="h-4 w-4" /> Add provider
                </Button>
              }
            />
          ) : (
            <>
              <div className="grid grid-cols-1 gap-vt-3 sm:grid-cols-2 lg:grid-cols-3">
                {accumulated.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setDrawerForId(p.id)}
                    className="text-left rounded-vt-md border border-vt-border bg-vt-surface p-vt-3 transition-colors duration-vt hover:bg-vt-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-vt-primary-400"
                  >
                    <div className="flex items-start gap-vt-3">
                      <ProviderPhoto providerId={p.id} hasPhoto={Boolean(p.photo_storage_key)} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-vt-2">
                          <span className="truncate font-semibold text-vt-text">{p.name}</span>
                          {p.featured ? (
                            <Sparkles className="h-3.5 w-3.5 text-vt-primary" aria-label="Featured" />
                          ) : null}
                          {p.is_society_verified ? (
                            <ShieldCheck className="h-3.5 w-3.5 text-vt-ok" aria-label="Verified" />
                          ) : null}
                        </div>
                        {p.business_name ? (
                          <div className="truncate text-vt-12 text-vt-text-muted">{p.business_name}</div>
                        ) : null}
                        <div className="mt-1 flex items-center gap-vt-2">
                          <Badge variant="muted">
                            {p.category?.name ?? categoryById.get(p.category_id)?.name ?? 'Uncategorised'}
                          </Badge>
                          <StarsInline avg={p.rating_avg} count={p.rating_count} />
                        </div>
                        <div className="mt-1 truncate text-vt-12 text-vt-text-muted">
                          {p.primary_phone}
                          {p.service_area ? ` · ${p.service_area}` : ''}
                        </div>
                        {p.emergency_available ? (
                          <div className="mt-1">
                            <Badge variant="warn">24×7 emergency</Badge>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <CursorPaginator
                hasMore={Boolean(nextCursor)}
                loading={listQuery.isFetching}
                itemsShown={accumulated.length}
                pageSize={PAGE_SIZE}
                onLoadMore={() => {
                  if (nextCursor) setCursor(nextCursor);
                }}
              />
            </>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : setOpen(false))}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit provider' : 'Add provider'}</DialogTitle>
            <DialogDescription>
              Fields marked optional can be filled in later. Residents see active providers only.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit((v) => save.mutate(v))} className="flex flex-col gap-vt-3">
            <div className="grid grid-cols-1 gap-vt-3 md:grid-cols-2">
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-category">Category</Label>
                <Select id="sp-category" {...form.register('category_id')}>
                  <option value="">— pick —</option>
                  {categories
                    .filter((c) => c.is_active)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </Select>
                {form.formState.errors.category_id ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.category_id.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-name">Name</Label>
                <Input id="sp-name" placeholder="Suresh Kumar" {...form.register('name')} />
                {form.formState.errors.name ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.name.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-biz">Business name (optional)</Label>
                <Input id="sp-biz" placeholder="Suresh Plumbing Works" {...form.register('business_name')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-phone">Primary phone</Label>
                <Input id="sp-phone" placeholder="+91 98XXXXXXXX" {...form.register('primary_phone')} />
                {form.formState.errors.primary_phone ? (
                  <p className="text-vt-12 text-vt-alert">{form.formState.errors.primary_phone.message}</p>
                ) : null}
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-phone2">Secondary phone</Label>
                <Input id="sp-phone2" {...form.register('secondary_phone')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-wa">WhatsApp</Label>
                <Input id="sp-wa" {...form.register('whatsapp')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-email">Email</Label>
                <Input id="sp-email" type="email" {...form.register('email')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-area">Service area</Label>
                <Input id="sp-area" placeholder="West Delhi" {...form.register('service_area')} />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="sp-addr">Address</Label>
                <Input id="sp-addr" {...form.register('address')} />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="sp-rate">Rate card</Label>
                <Textarea
                  id="sp-rate"
                  rows={2}
                  placeholder="Tap repair ₹250; complete bathroom ₹2500"
                  {...form.register('rate_card')}
                />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="sp-avail">Availability</Label>
                <Input id="sp-avail" placeholder="Mon–Sat 9 AM – 8 PM" {...form.register('availability')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-exp">Experience (years)</Label>
                <Input id="sp-exp" type="number" min={0} max={80} {...form.register('experience_years')} />
              </div>
              <div className="flex flex-col gap-vt-1">
                <Label htmlFor="sp-lang">Languages</Label>
                <Input id="sp-lang" placeholder="Hindi, English" {...form.register('languages_spoken')} />
              </div>
              <div className="flex flex-col gap-vt-1 md:col-span-2">
                <Label htmlFor="sp-notes">Notes (internal)</Label>
                <Textarea id="sp-notes" rows={2} {...form.register('notes')} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-vt-2 md:grid-cols-3">
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="checkbox"
                  {...form.register('emergency_available')}
                  className="h-4 w-4 accent-vt-primary"
                />
                24×7 emergency
              </label>
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="checkbox"
                  {...form.register('is_active')}
                  className="h-4 w-4 accent-vt-primary"
                />
                Active (residents see it)
              </label>
              <label className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                <input
                  type="checkbox"
                  {...form.register('featured')}
                  className="h-4 w-4 accent-vt-primary"
                />
                Featured
              </label>
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={save.isPending}
                loadingText="Saving…"
                disabled={save.isPending || !form.formState.isValid}
              >
                {editing ? 'Save changes' : 'Add provider'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={Boolean(confirmDeleteFor)}
        onOpenChange={(v) => {
          if (!v) setConfirmDeleteFor(null);
        }}
        title="Delete provider?"
        description={
          confirmDeleteFor
            ? `Delete ${confirmDeleteFor.name}? This is a soft delete (provider becomes invisible to residents). Existing ratings stay for audit.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => { if (confirmDeleteFor) remove.mutate(confirmDeleteFor.id); }}
      />

      {/* Right-side drawer */}
      {drawerForId ? (
        <ProviderDrawer
          providerId={drawerForId}
          onClose={() => setDrawerForId(null)}
          onEdit={(p) => {
            setDrawerForId(null);
            openEdit(p);
          }}
          onDelete={(p) => setConfirmDeleteFor(p)}
          onVerify={(id) => verify.mutate(id)}
          onToggleFeature={(id, featured) => toggleFeature.mutate({ id, featured })}
          onDeactivate={(id) => deactivate.mutate(id)}
        />
      ) : null}
    </div>
  );
}

// ─── drawer ──────────────────────────────────────────────────────────────

function ProviderDrawer({
  providerId,
  onClose,
  onEdit,
  onDelete,
  onVerify,
  onToggleFeature,
  onDeactivate
}: {
  providerId: string;
  onClose: () => void;
  onEdit: (p: ProviderRow) => void;
  onDelete: (p: ProviderRow) => void;
  onVerify: (id: string) => void;
  onToggleFeature: (id: string, featured: boolean) => void;
  onDeactivate: (id: string) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const detailQuery = useQuery({
    queryKey: ['service-provider-detail', providerId],
    queryFn: () => get<DetailResp>(`/service-providers/${providerId}`)
  });
  const provider = detailQuery.data ?? null;
  const ratings = provider?.ratings ?? [];

  const [showHidden, setShowHidden] = useState(false);
  const moderationQuery = useQuery({
    queryKey: ['service-provider-ratings', providerId, showHidden],
    queryFn: () =>
      get<RatingListResp>(`/service-providers/${providerId}/ratings`, {
        params: { include_hidden: showHidden, limit: 50 }
      }),
    enabled: showHidden
  });
  const moderationRatings = moderationQuery.data?.items;

  const fileRef = useRef<HTMLInputElement>(null);

  const uploadPhoto = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post(`/service-providers/${providerId}/photo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('Photo updated');
      void qc.invalidateQueries({ queryKey: ['service-provider-detail', providerId] });
      void qc.invalidateQueries({ queryKey: ['service-providers'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const hideRating = useMutation({
    mutationFn: (args: { rid: string; reason: string }) =>
      post(`/service-providers/ratings/${args.rid}/hide`, { hidden_reason: args.reason }),
    onSuccess: () => {
      toast.success('Rating hidden');
      void qc.invalidateQueries({ queryKey: ['service-provider-detail', providerId] });
      void qc.invalidateQueries({ queryKey: ['service-provider-ratings', providerId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });
  const unhideRating = useMutation({
    mutationFn: (rid: string) => post(`/service-providers/ratings/${rid}/unhide`, {}),
    onSuccess: () => {
      toast.success('Rating unhidden');
      void qc.invalidateQueries({ queryKey: ['service-provider-detail', providerId] });
      void qc.invalidateQueries({ queryKey: ['service-provider-ratings', providerId] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sp-drawer-title"
      className="fixed inset-0 z-50 flex"
    >
      <button
        type="button"
        className="flex-1 bg-vt-text/45 backdrop-blur-[4px]"
        onClick={onClose}
        aria-label="Close"
      />
      <aside className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-vt-border bg-vt-surface shadow-vt-3">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-vt-border bg-vt-surface px-vt-4 py-vt-3">
          <div className="min-w-0 flex-1">
            <h2 id="sp-drawer-title" className="truncate text-vt-16 font-semibold text-vt-text">
              {provider?.name ?? 'Loading…'}
            </h2>
            <p className="truncate text-vt-12 text-vt-text-muted">
              {provider?.category?.name ?? '—'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="inline-flex h-8 w-8 items-center justify-center rounded-vt-sm text-vt-text-muted hover:bg-vt-surface-2 hover:text-vt-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-col gap-vt-4 p-vt-4">
          {detailQuery.isLoading || !provider ? (
            <div className="space-y-2">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : (
            <>
              {/* Header card */}
              <Card>
                <CardContent className="p-vt-4 flex items-start gap-vt-3">
                  <ProviderPhoto
                    providerId={provider.id}
                    hasPhoto={Boolean(provider.photo_storage_key)}
                    size={72}
                  />
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-vt-2">
                      {provider.is_society_verified ? (
                        <Badge variant="ok">
                          <ShieldCheck className="h-3 w-3" /> Verified
                        </Badge>
                      ) : (
                        <Badge variant="muted">Unverified</Badge>
                      )}
                      {provider.featured ? <Badge variant="info">Featured</Badge> : null}
                      {provider.emergency_available ? <Badge variant="warn">24×7</Badge> : null}
                      {!provider.is_active ? <Badge variant="alert">Inactive</Badge> : null}
                    </div>
                    <div className="mt-1">
                      <StarsInline avg={provider.rating_avg} count={provider.rating_count} />
                    </div>
                    {provider.business_name ? (
                      <div className="mt-1 text-vt-12 text-vt-text-muted">{provider.business_name}</div>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-vt-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadPhoto.mutate(f);
                        if (fileRef.current) fileRef.current.value = '';
                      }}
                    />
                    <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()}>
                      <ImageIcon className="h-4 w-4" /> Photo
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Quick admin actions */}
              <div className="flex flex-wrap gap-vt-2">
                <Button size="sm" variant="primary" onClick={() => onEdit(provider)}>
                  <Edit3 className="h-4 w-4" /> Edit
                </Button>
                {!provider.is_society_verified ? (
                  <Button size="sm" variant="secondary" onClick={() => onVerify(provider.id)}>
                    <ShieldCheck className="h-4 w-4" /> Mark verified
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onToggleFeature(provider.id, !provider.featured)}
                >
                  <Sparkles className="h-4 w-4" />
                  {provider.featured ? 'Un-feature' : 'Feature'}
                </Button>
                {provider.is_active ? (
                  <Button size="sm" variant="secondary" onClick={() => onDeactivate(provider.id)}>
                    Deactivate
                  </Button>
                ) : null}
                <Button size="sm" variant="ghost" onClick={() => onDelete(provider)}>
                  <Trash2 className="h-4 w-4" /> Delete
                </Button>
              </div>

              {/* Contact + details */}
              <Card>
                <CardContent className="p-vt-4 grid grid-cols-1 gap-vt-2 text-vt-13 sm:grid-cols-2">
                  <div className="flex items-center gap-vt-2 text-vt-text">
                    <Phone className="h-4 w-4 text-vt-text-muted" />
                    <a href={`tel:${provider.primary_phone}`} className="hover:underline">
                      {provider.primary_phone}
                    </a>
                  </div>
                  {provider.secondary_phone ? (
                    <div className="flex items-center gap-vt-2 text-vt-text">
                      <Phone className="h-4 w-4 text-vt-text-muted" />
                      <a href={`tel:${provider.secondary_phone}`} className="hover:underline">
                        {provider.secondary_phone}
                      </a>
                    </div>
                  ) : null}
                  {provider.whatsapp ? (
                    <div className="flex items-center gap-vt-2 text-vt-text">
                      <MessageCircle className="h-4 w-4 text-vt-text-muted" />
                      <a
                        href={`https://wa.me/${provider.whatsapp.replace(/[^0-9]/g, '')}`}
                        target="_blank"
                        rel="noreferrer"
                        className="hover:underline"
                      >
                        {provider.whatsapp}
                      </a>
                    </div>
                  ) : null}
                  {provider.email ? (
                    <div className="flex items-center gap-vt-2 text-vt-text">
                      <Mail className="h-4 w-4 text-vt-text-muted" />
                      <a href={`mailto:${provider.email}`} className="hover:underline">
                        {provider.email}
                      </a>
                    </div>
                  ) : null}
                  {provider.address ? (
                    <div className="flex items-center gap-vt-2 text-vt-text sm:col-span-2">
                      <MapPin className="h-4 w-4 text-vt-text-muted" />
                      <span>{provider.address}</span>
                    </div>
                  ) : null}
                  {provider.service_area ? (
                    <div className="text-vt-text-muted">
                      <span className="font-medium text-vt-text">Service area:</span>{' '}
                      {provider.service_area}
                    </div>
                  ) : null}
                  {provider.availability ? (
                    <div className="text-vt-text-muted">
                      <span className="font-medium text-vt-text">Availability:</span>{' '}
                      {provider.availability}
                    </div>
                  ) : null}
                  {provider.experience_years != null ? (
                    <div className="text-vt-text-muted">
                      <span className="font-medium text-vt-text">Experience:</span>{' '}
                      {provider.experience_years} yrs
                    </div>
                  ) : null}
                  {provider.languages_spoken ? (
                    <div className="text-vt-text-muted">
                      <span className="font-medium text-vt-text">Languages:</span>{' '}
                      {provider.languages_spoken}
                    </div>
                  ) : null}
                </CardContent>
              </Card>

              {provider.rate_card ? (
                <Card>
                  <CardContent className="p-vt-4">
                    <div className="text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                      Rate card
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-vt-13 text-vt-text">
                      {provider.rate_card}
                    </p>
                  </CardContent>
                </Card>
              ) : null}

              {/* Ratings + moderation */}
              <Card>
                <CardContent className="p-vt-4">
                  <div className="flex items-center justify-between">
                    <div className="text-vt-12 font-semibold uppercase tracking-wide text-vt-text-muted">
                      Recent ratings
                    </div>
                    <label className="flex items-center gap-vt-2 text-vt-12 text-vt-text">
                      <input
                        type="checkbox"
                        checked={showHidden}
                        onChange={(e) => setShowHidden(e.target.checked)}
                        className="h-3.5 w-3.5 accent-vt-primary"
                      />
                      Show hidden
                    </label>
                  </div>
                  <div className="mt-vt-2 flex flex-col gap-vt-2">
                    {(showHidden ? moderationRatings ?? ratings : ratings).length === 0 ? (
                      <span className="text-vt-12 text-vt-text-muted">No ratings yet.</span>
                    ) : (
                      (showHidden ? moderationRatings ?? ratings : ratings).map((r) => (
                        <div
                          key={r.id}
                          className="rounded-vt-md border border-vt-border bg-vt-surface-2 p-vt-3"
                        >
                          <div className="flex items-center justify-between">
                            <div className="inline-flex items-center gap-1">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star
                                  key={i}
                                  className={
                                    i < r.stars
                                      ? 'h-3.5 w-3.5 fill-yellow-500 text-yellow-500'
                                      : 'h-3.5 w-3.5 text-vt-text-muted'
                                  }
                                />
                              ))}
                            </div>
                            <span className="text-vt-11 text-vt-text-muted">
                              {new Date(r.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          {r.review ? (
                            <p className="mt-1 text-vt-13 text-vt-text">{r.review}</p>
                          ) : null}
                          <div className="mt-1 flex items-center justify-between">
                            <div>
                              {r.is_hidden ? (
                                <Badge variant="alert">
                                  Hidden{r.hidden_reason ? ` — ${r.hidden_reason}` : ''}
                                </Badge>
                              ) : null}
                            </div>
                            <div className="flex items-center gap-vt-1">
                              {r.is_hidden ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => unhideRating.mutate(r.id)}
                                  title="Unhide"
                                >
                                  <Eye className="h-4 w-4" /> Unhide
                                </Button>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    const reason = window.prompt('Reason for hiding?');
                                    if (reason && reason.trim().length >= 2) {
                                      hideRating.mutate({ rid: r.id, reason: reason.trim() });
                                    }
                                  }}
                                  title="Hide"
                                >
                                  <EyeOff className="h-4 w-4" /> Hide
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
